import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, History, Pin, MessageSquare, ChevronRight, Menu, ChevronLeft } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ChatHistoryItem {
  id: string;
  title: string;
  messages: any[];
  lastUpdated: Date;
}

interface SidebarProps {
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  onNewChat: () => void;
  onSelectChat?: (chatId: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isSidebarOpen, 
  toggleSidebar, 
  onNewChat,
  onSelectChat = () => {} 
}) => {
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([]);
  const [favorites, setFavorites] = useState<ChatHistoryItem[]>([]);

  // Load chat history from localStorage when component mounts
  useEffect(() => {
    loadChatHistory();
  }, []);

  const loadChatHistory = () => {
    try {
      const savedChats = localStorage.getItem('chatHistory');
      if (savedChats) {
        // Attempt to parse the saved chats
        let parsedChats: ChatHistoryItem[] = [];
        try {
          parsedChats = JSON.parse(savedChats);
          // Validate the parsed data structure here if necessary
          if (!Array.isArray(parsedChats)) {
            console.error('Chat history is not an array.');
            throw new Error('Invalid chat history format');
          }
        } catch (parseError) {
          console.error('Error parsing chat history JSON:', parseError);
          // Clear the corrupted item and reset state
          localStorage.removeItem('chatHistory');
          setChatHistory([]);
          setFavorites([]);
          return; // Exit the function if parsing failed
        }

        // Format dates properly, checking if lastUpdated exists and is valid
        const formattedChats = parsedChats.map(chat => {
          const lastUpdated = chat.lastUpdated ? new Date(chat.lastUpdated) : new Date();
          return {
            ...chat,
            // Ensure date conversion is robust, default to now if invalid
            lastUpdated: isNaN(lastUpdated.getTime()) ? new Date() : lastUpdated 
          };
        });

        setChatHistory(formattedChats);

        // For now, keep favorites empty or implement favorite functionality
        setFavorites([]);
      }
    } catch (error) {
      // Catch any other potential errors during loading (e.g., localStorage access)
      console.error('Error loading chat history from localStorage:', error);
      setChatHistory([]);
      setFavorites([]);
    }
  };

  const getRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) {
      return diffInMinutes <= 1 ? 'just now' : `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays < 7) {
      return diffInDays === 1 ? 'Yesterday' : `${diffInDays} days ago`;
    } else {
      return `${Math.floor(diffInDays / 7)} weeks ago`;
    }
  };

  const handleChatSelect = (chatId: string) => {
    onSelectChat(chatId);
    // Close the sidebar on mobile after selecting a chat
    if (window.innerWidth < 1024) {
      toggleSidebar();
    }
  };

  return (
    <>
      {/* Sidebar Toggle Button for mobile and desktop */}
      <Button 
        variant="ghost" 
        size="icon" 
        onClick={toggleSidebar}
        className={`fixed top-4 left-4 z-50 ${isSidebarOpen ? 'left-[260px] lg:left-[260px]' : 'left-4'} transition-all duration-300`}
      >
        {isSidebarOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
      </Button>
    
      <div 
        className={`fixed inset-y-0 left-0 z-30 w-72 transform transition-transform duration-300 ease-in-out 
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          border-r border-white/10 h-screen backdrop-blur-lg bg-black/30`}
        style={{
          boxShadow: '0 0 15px rgba(138, 43, 226, 0.3)',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <div className="flex flex-col h-full p-4 pt-14">
          {/* AI+ Badge Image above New Chat button */}
          <div className="flex justify-center mb-3">
            <img src="/panel-logo.png" alt="AI Plus" className="h-16" />
          </div>
          
          <Button 
            className="w-full mb-4 grok-button flex items-center justify-center bg-purple-600/80 backdrop-blur-md hover:bg-purple-600"
            onClick={onNewChat}
            style={{
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}
          >
            <div className="flex items-center justify-center">
              <Plus size={16} className="mr-2" /> 
              <span>NEW CHAT</span>
            </div>
          </Button>
          
          <ScrollArea className="flex-1">
            <Accordion type="multiple" defaultValue={["history", "favorites"]} className="w-full">
              <AccordionItem value="history" className="border-b-0">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center">
                    <History size={16} className="mr-2" />
                    <span className="text-sm font-medium">History</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-1">
                    {chatHistory.length === 0 ? (
                      <p className="text-sm text-muted-foreground px-2 py-1">No chat history yet</p>
                    ) : (
                      chatHistory.map((chat) => (
                        <Button
                          key={chat.id}
                          variant="ghost"
                          className="w-full justify-between text-left h-auto py-2 px-2 group hover:bg-white/10"
                          onClick={() => handleChatSelect(chat.id)}
                        >
                          <div className="flex items-start">
                            <MessageSquare size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium truncate max-w-[180px]">{chat.title}</p>
                              <p className="text-xs text-muted-foreground">{getRelativeTime(chat.lastUpdated)}</p>
                            </div>
                          </div>
                          <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Button>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
              
              <AccordionItem value="favorites" className="border-b-0">
                <AccordionTrigger className="py-2 hover:no-underline">
                  <div className="flex items-center">
                    <Pin size={16} className="mr-2" />
                    <span className="text-sm font-medium">Favorites</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-1">
                    {favorites.length === 0 ? (
                      <p className="text-sm text-muted-foreground px-2 py-1">No favorites yet</p>
                    ) : (
                      favorites.map((chat) => (
                        <Button
                          key={chat.id}
                          variant="ghost"
                          className="w-full justify-between text-left h-auto py-2 px-2 group hover:bg-white/10"
                          onClick={() => handleChatSelect(chat.id)}
                        >
                          <div className="flex items-start">
                            <MessageSquare size={14} className="mr-2 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium truncate max-w-[180px]">{chat.title}</p>
                              <p className="text-xs text-muted-foreground">{getRelativeTime(chat.lastUpdated)}</p>
                            </div>
                          </div>
                          <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                        </Button>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </ScrollArea>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
